import React, { useContext, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import dayjs from "dayjs";

import AddIcon from "@mui/icons-material/Add";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { DatabaseContext, UserContext } from "../../context";
import firebase from "../../firebase";
import { makeid } from "../../utility";
import NodiantCalendar from "../calendar/NodiantCalendar";
import DialogWithTextInput from "../DialogWithTextInput";
import ToolbarWithLogo from "../ToolbarWithLogo";

const surfaceSx = {
    borderRadius: 3,
    borderColor: "divider",
    backgroundColor: "background.paper",
    boxShadow: "0 8px 28px rgba(20, 35, 25, 0.065)",
};

function formatDate(value) {
    const date = dayjs(value);
    return date.isValid() ? date.format("D MMM YYYY") : "Date unavailable";
}

function CalendarFilter({ date, label, onChange, onClear }) {
    return (
        <Card
            variant="outlined"
            sx={{
                borderRadius: 2.5,
                borderColor: "divider",
                boxShadow: "none",
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    minHeight: 58,
                    px: 2,
                    py: 1.25,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "background.subtle",
                }}
            >
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        borderRadius: 1.5,
                        color: "primary.main",
                        backgroundColor: "rgba(0, 149, 61, 0.09)",
                    }}
                >
                    <CalendarMonthOutlinedIcon fontSize="small" />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        {label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {date ? formatDate(date) : "Any date"}
                    </Typography>
                </Box>
                {date && (
                    <Button
                        size="small"
                        color="inherit"
                        startIcon={<ClearOutlinedIcon />}
                        onClick={onClear}
                    >
                        Clear
                    </Button>
                )}
            </Box>
            <Box sx={{ p: { xs: 1.25, sm: 1.75 } }}>
                <NodiantCalendar
                    selectedDate={date || null}
                    onDateChange={onChange}
                />
            </Box>
        </Card>
    );
}

function ProjectRow({ project, creatorName, onEdit, onView }) {
    return (
        <Box
            sx={{
                p: { xs: 2, sm: 2.25 },
                display: "flex",
                alignItems: { xs: "flex-start", md: "center" },
                flexDirection: { xs: "column", md: "row" },
                gap: 2,
                transition: "background-color 120ms ease",
                "&:hover": { backgroundColor: "action.hover" },
            }}
        >
            <Box sx={{ display: "flex", gap: 1.5, flex: 1, minWidth: 0 }}>
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        borderRadius: 2,
                        color: "primary.main",
                        backgroundColor: "rgba(0, 149, 61, 0.09)",
                    }}
                >
                    <FolderOutlinedIcon />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: 800,
                            lineHeight: 1.25,
                            overflowWrap: "anywhere",
                        }}
                    >
                        {project.title || "Untitled project"}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.35 }}
                    >
                        By {creatorName} · Updated {formatDate(project.lastUpdated)}
                    </Typography>
                </Box>
            </Box>
            <Box
                sx={{
                    width: { xs: "100%", md: "auto" },
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    pl: { xs: 7.25, md: 0 },
                }}
            >
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditNoteOutlinedIcon />}
                    onClick={onEdit}
                    disabled={!project.canEdit}
                >
                    Edit project
                </Button>
                <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    startIcon={<VisibilityOutlinedIcon />}
                    onClick={onView}
                    disabled={!project.canView}
                >
                    View project
                </Button>
            </Box>
        </Box>
    );
}

function ProjectSection({
    title,
    description,
    projects,
    emptyMessage,
    userByKey,
    onEdit,
    onView,
}) {
    return (
        <Card variant="outlined" sx={surfaceSx}>
            <Box
                sx={{
                    px: { xs: 2, sm: 3 },
                    py: 2.25,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                }}
            >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" component="h2">
                        {title}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.35 }}
                    >
                        {description}
                    </Typography>
                </Box>
                <Chip
                    size="small"
                    label={projects.length}
                    sx={{
                        minWidth: 34,
                        backgroundColor: "rgba(0, 149, 61, 0.09)",
                        color: "primary.main",
                    }}
                />
            </Box>
            <Divider />
            {projects.length === 0 ? (
                <Box
                    sx={{
                        minHeight: 170,
                        px: 3,
                        py: 4,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "text.secondary",
                        textAlign: "center",
                    }}
                >
                    <InboxOutlinedIcon
                        sx={{ mb: 1, fontSize: 40, opacity: 0.42 }}
                    />
                    <Typography variant="subtitle1" color="text.primary">
                        {emptyMessage}
                    </Typography>
                </Box>
            ) : (
                projects.map((project, index) => (
                    <React.Fragment key={project.key}>
                        {index > 0 && <Divider />}
                        <ProjectRow
                            project={project}
                            creatorName={
                                userByKey.get(project.createdBy)?.fullName ||
                                "Unknown user"
                            }
                            onEdit={onEdit(project)}
                            onView={onView(project)}
                        />
                    </React.Fragment>
                ))
            )}
        </Card>
    );
}

export default function ProjectSelectPage() {
    const { currentUserData } = useContext(UserContext);
    const {
        projectInfo = [],
        projectInfoLoaded = false,
        users = [],
        groups = [],
        groupsLoaded = false,
    } = useContext(DatabaseContext);
    const history = useHistory();

    const [fromDate, setFromDate] = useState();
    const [toDate, setToDate] = useState();
    const [dateSearchEnabled, setDateSearchEnabled] = useState(false);
    const [searchedProjects, setSearchedProjects] = useState([]);
    const [projectNameDialogOpen, setProjectNameDialogOpen] = useState(false);
    const [searchResultsVisible, setSearchResultsVisible] = useState(false);
    const [projectNameSearch, setProjectNameSearch] = useState("");
    const [userNameSearch, setUserNameSearch] = useState("");

    const userByKey = useMemo(
        () => new Map(users.map((user) => [user.key, user])),
        [users],
    );

    const {
        availableProjects,
        myProjects,
        projectsSharedWithMe,
    } = useMemo(() => {
        const emptyProjectLists = {
            availableProjects: [],
            myProjects: [],
            projectsSharedWithMe: [],
        };

        if (!currentUserData) {
            return emptyProjectLists;
        }

        const myProjectsArray = [];
        const sharedProjectsArray = [];
        const availableProjectsArray = [];

        projectInfo.forEach((project) => {
            const projectWithPermissions = {
                ...project,
                canEdit: false,
                canView: false,
            };

            if (project.createdBy === currentUserData.key) {
                projectWithPermissions.canEdit = true;
                projectWithPermissions.canView = true;
                myProjectsArray.push(projectWithPermissions);
                availableProjectsArray.push(projectWithPermissions);
                return;
            }

            const matchingUser = (project.users || []).find(
                (user) => user.key === currentUserData.key,
            );
            if (matchingUser) {
                projectWithPermissions.canEdit = Boolean(matchingUser.canEdit);
                projectWithPermissions.canView = true;
            }

            (project.groups || []).forEach((projectGroup) => {
                const matchingGroup = groups.find(
                    (group) => group.key === projectGroup.key,
                );
                const isMember = (matchingGroup?.users || []).some(
                    (user) => user.key === currentUserData.key,
                );
                if (isMember) {
                    projectWithPermissions.canEdit = Boolean(
                        projectGroup.canEdit,
                    );
                    projectWithPermissions.canView = true;
                }
            });

            if (
                projectWithPermissions.canEdit ||
                projectWithPermissions.canView
            ) {
                sharedProjectsArray.push(projectWithPermissions);
                availableProjectsArray.push(projectWithPermissions);
            }
        });

        return {
            availableProjects: availableProjectsArray,
            myProjects: myProjectsArray,
            projectsSharedWithMe: sharedProjectsArray,
        };
    }, [currentUserData, groups, projectInfo]);

    const canCreateProjects = Boolean(
        currentUserData?.permissions?.createProjects,
    );
    const isProjectsLoading =
        !currentUserData || !groupsLoaded || !projectInfoLoaded;

    const handleEditProjectSelect = (project) => () => {
        history.push(`/projects/e?key=${project.key}`);
    };

    const handleViewProjectSelect = (project) => () => {
        history.push(`/projects/v?key=${project.key}`);
    };

    function handleCloseProjectNameDialog(newName, okPressed) {
        if (!okPressed) {
            setProjectNameDialogOpen(false);
            return;
        }

        const projectName = newName;
        if (projectName !== "") {
            const infoRef = firebase.database().ref("projectinfo");
            const key = makeid(15);

            infoRef.child(key).set({
                createdBy: currentUserData.key,
                createdOn: dayjs().toString(),
                lastUpdated: dayjs().toString(),
                intro: "",
                title: projectName,
            });

            setProjectNameDialogOpen(false);
            history.push(`/projects/e?key=${key}`);
        }
    }

    function handleSearchPressed() {
        const projectNameTerm = projectNameSearch.trim().toLowerCase();
        const userNameTerm = userNameSearch.trim().toLowerCase();
        const fromBoundary =
            dateSearchEnabled && fromDate ? dayjs(fromDate) : null;
        const toBoundary =
            dateSearchEnabled && toDate ? dayjs(toDate) : null;

        setSearchedProjects(
            availableProjects.filter((project) => {
                if (
                    projectNameTerm &&
                    !(project.title || "")
                        .toLowerCase()
                        .includes(projectNameTerm)
                ) {
                    return false;
                }

                const creatorName =
                    userByKey.get(project.createdBy)?.fullName || "";
                if (
                    userNameTerm &&
                    !creatorName.toLowerCase().includes(userNameTerm)
                ) {
                    return false;
                }

                const lastUpdated = dayjs(project.lastUpdated);
                if (fromBoundary && lastUpdated.isBefore(fromBoundary)) {
                    return false;
                }
                if (toBoundary && lastUpdated.isAfter(toBoundary)) {
                    return false;
                }
                return true;
            }),
        );
        setSearchResultsVisible(true);
    }

    function handleClearSearch() {
        setProjectNameSearch("");
        setUserNameSearch("");
        setDateSearchEnabled(false);
        setFromDate(undefined);
        setToDate(undefined);
        setSearchedProjects([]);
        setSearchResultsVisible(false);
    }

    const hasSearchFilters = Boolean(
        projectNameSearch ||
            userNameSearch ||
            (dateSearchEnabled && (fromDate || toDate)),
    );

    function handleDateSearchToggle(event) {
        const enabled = event.target.checked;
        setDateSearchEnabled(enabled);

        if (!enabled) {
            setFromDate(undefined);
            setToDate(undefined);
        }
    }

    return (
        <>
            <Box
                sx={{
                    minHeight: {
                        xs: "calc(100dvh - 60px)",
                        sm: "calc(100vh - 128px)",
                    },
                    pb: 12,
                    backgroundColor: "background.default",
                }}
            >
                <Container
                    maxWidth="lg"
                    sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 3 } }}
                >
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        <Card variant="outlined" sx={surfaceSx}>
                            <CardContent
                                sx={{
                                    p: { xs: 2.5, sm: 3 },
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    "&:last-child": { pb: { xs: 2.5, sm: 3 } },
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 46,
                                        height: 46,
                                        display: "grid",
                                        placeItems: "center",
                                        flexShrink: 0,
                                        borderRadius: 2,
                                        color: "primary.main",
                                        backgroundColor: "rgba(0, 149, 61, 0.09)",
                                    }}
                                >
                                    <FolderOutlinedIcon />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="h5" component="h1">
                                        Projects
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mt: 0.5 }}
                                    >
                                        Find, view, and manage the projects available to you.
                                    </Typography>
                                </Box>
                                <Chip
                                    size="small"
                                    label={
                                        isProjectsLoading
                                            ? "Loading…"
                                            : `${availableProjects.length} available`
                                    }
                                    sx={{
                                        display: { xs: "none", sm: "flex" },
                                        backgroundColor: "rgba(0, 149, 61, 0.09)",
                                        color: "primary.main",
                                    }}
                                />
                                {!isProjectsLoading && canCreateProjects && (
                                    <Button
                                        variant="contained"
                                        disableElevation
                                        startIcon={<AddIcon />}
                                        onClick={() =>
                                            setProjectNameDialogOpen(true)
                                        }
                                    >
                                        New project
                                    </Button>
                                )}
                            </CardContent>
                        </Card>

                        {isProjectsLoading ? (
                            <Card variant="outlined" sx={surfaceSx}>
                                <Box
                                    sx={{
                                        minHeight: 320,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 1.5,
                                        color: "text.secondary",
                                    }}
                                >
                                    <CircularProgress
                                        size={32}
                                        aria-label="Loading projects"
                                    />
                                    <Typography variant="body2">
                                        Loading your projects…
                                    </Typography>
                                </Box>
                            </Card>
                        ) : (
                            <>
                                <Card variant="outlined" sx={surfaceSx}>
                                    <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.25 }}>
                                        <Typography variant="h6" component="h2">
                                            Search projects
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ mt: 0.35 }}
                                        >
                                            Filter by title, creator, or the date a project was last updated.
                                        </Typography>
                                    </Box>
                                    <Divider />
                                    <Box
                                        sx={{
                                            p: { xs: 2, sm: 3 },
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 2.5,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: {
                                                    xs: "minmax(0, 1fr)",
                                                    sm: "repeat(2, minmax(0, 1fr))",
                                                },
                                                gap: 2,
                                            }}
                                        >
                                            <TextField
                                                fullWidth
                                                value={projectNameSearch}
                                                id="projectNameSearch"
                                                onChange={(event) =>
                                                    setProjectNameSearch(
                                                        event.target.value,
                                                    )
                                                }
                                                onKeyDown={(event) => {
                                                    if (event.key === "Enter") {
                                                        handleSearchPressed();
                                                    }
                                                }}
                                                label="Project name"
                                            />
                                            <TextField
                                                fullWidth
                                                value={userNameSearch}
                                                id="userNameSearch"
                                                onChange={(event) =>
                                                    setUserNameSearch(
                                                        event.target.value,
                                                    )
                                                }
                                                onKeyDown={(event) => {
                                                    if (event.key === "Enter") {
                                                        handleSearchPressed();
                                                    }
                                                }}
                                                label="Created by"
                                            />
                                        </Box>
                                        <Box
                                            sx={{
                                                minHeight: 64,
                                                px: 2,
                                                py: 1.25,
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1.5,
                                                border: "1px solid",
                                                borderColor: "divider",
                                                borderRadius: 2.5,
                                                backgroundColor: "background.subtle",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 36,
                                                    height: 36,
                                                    display: "grid",
                                                    placeItems: "center",
                                                    flexShrink: 0,
                                                    borderRadius: 1.5,
                                                    color: "primary.main",
                                                    backgroundColor:
                                                        "rgba(0, 149, 61, 0.09)",
                                                }}
                                            >
                                                <CalendarMonthOutlinedIcon fontSize="small" />
                                            </Box>
                                            <Box
                                                sx={{ flex: 1, minWidth: 0 }}
                                            >
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{ fontWeight: 800 }}
                                                >
                                                    Search by date
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    Filter projects by when
                                                    they were last updated.
                                                </Typography>
                                            </Box>
                                            <Switch
                                                checked={dateSearchEnabled}
                                                onChange={
                                                    handleDateSearchToggle
                                                }
                                                inputProps={{
                                                    "aria-label":
                                                        "Search projects by date",
                                                }}
                                            />
                                        </Box>

                                        {dateSearchEnabled && (
                                            <Box
                                                sx={{
                                                    display: "grid",
                                                    gridTemplateColumns: {
                                                        xs: "minmax(0, 1fr)",
                                                        md: "repeat(2, minmax(0, 1fr))",
                                                    },
                                                    gap: 2,
                                                }}
                                            >
                                                <CalendarFilter
                                                    label="Updated from"
                                                    date={fromDate}
                                                    onChange={setFromDate}
                                                    onClear={() =>
                                                        setFromDate(undefined)
                                                    }
                                                />
                                                <CalendarFilter
                                                    label="Updated to"
                                                    date={toDate}
                                                    onChange={setToDate}
                                                    onClear={() =>
                                                        setToDate(undefined)
                                                    }
                                                />
                                            </Box>
                                        )}
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "flex-end",
                                                flexWrap: "wrap",
                                                gap: 1,
                                            }}
                                        >
                                            {hasSearchFilters && (
                                                <Button
                                                    color="inherit"
                                                    startIcon={
                                                        <ClearOutlinedIcon />
                                                    }
                                                    onClick={handleClearSearch}
                                                >
                                                    Clear filters
                                                </Button>
                                            )}
                                            <Button
                                                variant="contained"
                                                disableElevation
                                                startIcon={
                                                    <SearchOutlinedIcon />
                                                }
                                                onClick={handleSearchPressed}
                                            >
                                                Search
                                            </Button>
                                        </Box>
                                    </Box>
                                </Card>

                                {searchResultsVisible && (
                                    <ProjectSection
                                        title="Search results"
                                        description="Projects matching your selected filters."
                                        projects={searchedProjects}
                                        emptyMessage="No projects match these filters"
                                        userByKey={userByKey}
                                        onEdit={handleEditProjectSelect}
                                        onView={handleViewProjectSelect}
                                    />
                                )}
                                {myProjects.length > 0 && (
                                    <ProjectSection
                                        title="My projects"
                                        description="Projects you created and can manage."
                                        projects={myProjects}
                                        emptyMessage="You have not created any projects"
                                        userByKey={userByKey}
                                        onEdit={handleEditProjectSelect}
                                        onView={handleViewProjectSelect}
                                    />
                                )}
                                {projectsSharedWithMe.length > 0 && (
                                    <ProjectSection
                                        title="Shared with me"
                                        description="Projects shared with you directly or through a group."
                                        projects={projectsSharedWithMe}
                                        emptyMessage="No projects have been shared with you"
                                        userByKey={userByKey}
                                        onEdit={handleEditProjectSelect}
                                        onView={handleViewProjectSelect}
                                    />
                                )}
                                {myProjects.length === 0 &&
                                    projectsSharedWithMe.length === 0 && (
                                        <Card variant="outlined" sx={surfaceSx}>
                                            <Box
                                                sx={{
                                                    minHeight: 220,
                                                    px: 3,
                                                    py: 5,
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    textAlign: "center",
                                                    color: "text.secondary",
                                                }}
                                            >
                                                <InboxOutlinedIcon
                                                    sx={{
                                                        mb: 1.25,
                                                        fontSize: 44,
                                                        opacity: 0.42,
                                                    }}
                                                />
                                                <Typography
                                                    variant="h6"
                                                    color="text.primary"
                                                >
                                                    No projects available
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ mt: 0.5 }}
                                                >
                                                    {canCreateProjects
                                                        ? "Create a project to get started."
                                                        : "Projects shared with you will appear here."}
                                                </Typography>
                                                {canCreateProjects && (
                                                    <Button
                                                        sx={{ mt: 2 }}
                                                        variant="contained"
                                                        disableElevation
                                                        startIcon={<AddIcon />}
                                                        onClick={() =>
                                                            setProjectNameDialogOpen(
                                                                true,
                                                            )
                                                        }
                                                    >
                                                        New project
                                                    </Button>
                                                )}
                                            </Box>
                                        </Card>
                                    )}
                            </>
                        )}
                    </Box>
                </Container>
            </Box>
            <ToolbarWithLogo />
            <DialogWithTextInput
                open={projectNameDialogOpen}
                onClose={handleCloseProjectNameDialog}
                title="Create project"
                message="Give your project a clear name. You can add the description and content next."
                initialValue=""
                inputLabel="Project name"
                confirmLabel="Create project"
                headerIcon={<FolderOutlinedIcon />}
                maxLength={120}
                modern
            />
        </>
    );
}
